/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { TemporaryDrawer } from '@coursera/cds-core';

import ExportDrawerDateSelectionView from 'bundles/enterprise-admin-drawers/components/ExportDrawerDateSelectionView';
import type { DateRangeFilterOption } from 'bundles/enterprise-admin-drawers/components/types';
import type { UsageReportV2Options } from 'bundles/enterprise-admin-drawers/utils/EnterpriseEmailReportsAPIUtils';

import type { CreateReportScopeKey } from '../utils/EnterpriseEmailReportsAPIUtils';
import ExportDrawerProgramSelectionView from './ExportDrawerProgramSelectionView';
import ExportDrawerSelectionView from './ExportDrawerSelectionView';
import type { ExportFilters, ProgramList } from './types';

//
// would be nice to refactor these props to be provided in some other way.
// I've left this extracted from props to indicate this desire ;D
//
type FilterProps = {
  organizationId: string;
  programs: ProgramList;
};

export type Props = FilterProps & {
  isOpen: boolean;

  /**
   * The filter values.
   */
  filters: ExportFilters;

  /**
   * The drawer wants to be closed from some action (e.g. cancel).
   */
  onRequestClose: () => void;

  /**
   * Called when the export is requested.
   */
  onExport: (filters: UsageReportV2Options) => void;

  /**
   * Primary title of the drawer diaplayed at the root level.
   */
  title: string;

  /**
   * Optional component to override the select filter (main) view.
   */
  extraSelectFilterViewComponent?: React.ReactNode;

  /**
   * Optional component to append on top of the actions.
   */
  extraSelectFilterViewAction?: React.ReactNode;

  reportScope?: CreateReportScopeKey;
};

export type ProgramSelectionMap = {
  [id: string]: { id: string; name: string; isSelected: boolean };
};

const EXPORT_DRAWER_STEPS = {
  FILTER_SELECTION_VIEW: 0,
  PROGRAM_SELECTION_VIEW: 1,
  DATE_SELECTION_VIEW: 2,
};

export const ExportDrawer: React.FC<Props> = ({
  filters,
  onRequestClose,
  title,
  programs,
  onExport,
  isOpen,
  extraSelectFilterViewComponent,
  extraSelectFilterViewAction,
  reportScope,
}) => {
  const initialProgramsMap = programs.reduce(
    (acc, program) => ({ ...acc, [program.id]: { ...program, isSelected: false } }),
    {}
  );

  const [navIndex, setNavIndex] = useState<number>(EXPORT_DRAWER_STEPS.FILTER_SELECTION_VIEW);
  const [selectedProgramsMap, setSelectedProgramsMap] = useState<ProgramSelectionMap>(initialProgramsMap);
  const [dateRange, setDateRange] = useState<DateRangeFilterOption | undefined | null>(filters.dateRange);
  const [shouldIncludeDeletedMembers, setShouldIncludeDeletedMembers] = useState<boolean>(
    Boolean(filters.options.includeDeletedMembers)
  );
  const [shouldExportOneCSVPerProgram, setShouldExportOneCSVPerProgram] = useState<boolean>(
    Boolean(filters.options.oneCsvPerProgram)
  );

  React.useEffect(() => {
    setSelectedProgramsMap(
      programs.reduce((acc, program) => ({ ...acc, [program.id]: { ...program, isSelected: false } }), {})
    );
  }, [programs]);

  const handleOnClose = () => {
    onRequestClose();
    setNavIndex(EXPORT_DRAWER_STEPS.FILTER_SELECTION_VIEW);
    setSelectedProgramsMap(initialProgramsMap);
    setDateRange(filters.dateRange);
  };

  /**
   * The apply action handler for a filter view.
   */
  const handleDateRangeOnApply = (newDateRange?: DateRangeFilterOption | null) => {
    setDateRange(newDateRange);
    setNavIndex(EXPORT_DRAWER_STEPS.FILTER_SELECTION_VIEW);
  };

  const handleProgramSelection = (newSelectionMap: ProgramSelectionMap) => {
    setSelectedProgramsMap(newSelectionMap);
    setNavIndex(EXPORT_DRAWER_STEPS.FILTER_SELECTION_VIEW);
  };

  const handleExport = () => {
    const selectedProgramIds = programs.reduce(
      (acc, program) => (selectedProgramsMap[program.id].isSelected ? [...acc, program.id] : acc),
      [] as string[]
    );

    // Include all programs if no programs are selected
    const newFilters: UsageReportV2Options = {
      ...(dateRange || {}),
      programIds: selectedProgramIds.length ? selectedProgramIds : programs.map((program) => program.id),
      oneCsvPerProgram: shouldExportOneCSVPerProgram,
      includeDeletedMembers: shouldIncludeDeletedMembers,
      reportScope,
    };

    onExport(newFilters);
  };

  const renderCurrentStep = () => {
    if (navIndex === EXPORT_DRAWER_STEPS.FILTER_SELECTION_VIEW) {
      return (
        <ExportDrawerSelectionView
          title={title}
          selectedPrograms={programs.filter((program) => selectedProgramsMap[program.id]?.isSelected)}
          oneCsvPerProgram={shouldExportOneCSVPerProgram}
          includeDeletedMembers={shouldIncludeDeletedMembers}
          onIncludeDeletedMembersChange={() => setShouldIncludeDeletedMembers((prevState) => !prevState)}
          onOneCSVPerProgramChange={() => setShouldExportOneCSVPerProgram((prevState) => !prevState)}
          onExport={handleExport}
          handleDateRangeViewSelection={() => setNavIndex(EXPORT_DRAWER_STEPS.DATE_SELECTION_VIEW)}
          handleProgramViewSelection={() => setNavIndex(EXPORT_DRAWER_STEPS.PROGRAM_SELECTION_VIEW)}
          onClose={handleOnClose}
          dateRange={dateRange}
          extraSelectFilterViewComponent={extraSelectFilterViewComponent}
          extraSelectFilterViewAction={extraSelectFilterViewAction}
        />
      );
    } else if (navIndex === EXPORT_DRAWER_STEPS.PROGRAM_SELECTION_VIEW) {
      return (
        <ExportDrawerProgramSelectionView
          programs={programs}
          selectedProgramsMap={selectedProgramsMap}
          onBack={() => setNavIndex(EXPORT_DRAWER_STEPS.FILTER_SELECTION_VIEW)}
          onApply={(newSelectionMap: ProgramSelectionMap) => handleProgramSelection(newSelectionMap)}
          onClose={handleOnClose}
        />
      );
    } else {
      return (
        <ExportDrawerDateSelectionView
          selectedDateRange={dateRange}
          onBack={() => setNavIndex(EXPORT_DRAWER_STEPS.FILTER_SELECTION_VIEW)}
          onApply={handleDateRangeOnApply}
          onClose={handleOnClose}
        />
      );
    }
  };

  return (
    <TemporaryDrawer open={isOpen} anchor="right" onClose={handleOnClose} sticky="actions">
      {renderCurrentStep()}
    </TemporaryDrawer>
  );
};

export default ExportDrawer;
